'use client';

import { useDrawer, useModal } from '@cat-ecom/pcc-components';
import {
  createContext,
  useContext,
  FC,
  PropsWithChildren,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
  useCallback
} from 'react';
import {
  LOGIN_FROM_HP,
  RP_COOKIE
} from 'services/dealer-association/constants';
import { deleteCookie, setCookie } from 'utils/cookieUtils';
import { MICO_COOKIE } from 'constants/storageConstants';

type Modal = ReturnType<typeof useModal>;
type Drawer = ReturnType<typeof useDrawer>;
type entitlementResponse = {
  redirectURL: string;
  isMicoCookieRequired: boolean;
};
type DrawersContext = {
  isLoading: boolean;
  showLoader: Dispatch<SetStateAction<boolean>>;
  associatedDealerModal: Modal;
  associatedDealerDrawer: Drawer;
  searchResultsDrawer: Drawer;
  selectCustomerDrawer: Drawer;
  searchResultsModal: Modal;
  selectCustomerModal: Modal;
  onSuccessHandler: (response: entitlementResponse) => void;
};

//Create context
const AssociatedDealerContext = createContext<DrawersContext>(null);

export const useAssociatedDealer = () => useContext(AssociatedDealerContext);

type NextProviderProps = PropsWithChildren;
// create Provider
export const AssociatedDealerProvider: FC<NextProviderProps> = ({
  children
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const associatedDealerModal = useModal({ isRequired: true, size: 'md' });
  const associatedDealerDrawer = useDrawer(); // Main drawer; either for select-customer or dealer-search drawer
  const searchResultsDrawer = useDrawer();
  const selectCustomerDrawer = useDrawer();
  const searchResultsModal = useModal({ isRequired: true, size: 'md' });
  const selectCustomerModal = useModal({ isRequired: true, size: 'md' });

  const onSuccessHandler = useCallback((response) => {
    if (!response?.isDeleteCookies) {
      deleteCookie(LOGIN_FROM_HP);
      deleteCookie(RP_COOKIE);
    }

    if (response.isMicoCookieRequired) {
      setCookie(MICO_COOKIE, 1);
    }
    window.location = response.redirectURL;
  }, []);

  const context = useMemo(() => {
    return {
      isLoading,
      showLoader: setIsLoading,
      associatedDealerModal,
      associatedDealerDrawer,
      searchResultsDrawer,
      selectCustomerDrawer,
      searchResultsModal,
      selectCustomerModal,
      onSuccessHandler
    };
  }, [
    isLoading,
    setIsLoading,
    associatedDealerModal,
    associatedDealerDrawer,
    searchResultsDrawer,
    selectCustomerDrawer,
    searchResultsModal,
    selectCustomerModal,
    onSuccessHandler
  ]);

  return (
    <AssociatedDealerContext.Provider value={context}>
      {children}
    </AssociatedDealerContext.Provider>
  );
};
